import { BracketsCurly } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/Button';
import { VariablesDropdownMenu } from 'src/components/VariablesDropdownMenu';
import { cn } from 'src/utils/className';

type AddVariableButtonProps = {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  variables?: string[];
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  className?: string;
  buttonVariant?: 'text' | 'contained' | 'outlined';
  buttonSize?: 'small' | 'medium' | 'large';
  disabled?: boolean;
};

export const AddVariableButton = ({
  textareaRef,
  variables = [],
  position = 'top-left',
  className,
  buttonVariant = 'text',
  buttonSize = 'small',
  disabled = false,
}: AddVariableButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideButton = !!buttonRef.current?.contains(targetNode);
      const clickedInsideMenu =
        !!menuContainerRef.current?.contains(targetNode);
      if (!clickedInsideButton && !clickedInsideMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const insertAtCursor = (newText: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const content = textarea.value ?? '';
    const selectionEnd = textarea.selectionEnd;

    // Prefer inserting after selection
    let insertionIndex = selectionEnd;

    // If the caret/selection is inside an existing variable token { ... },
    // move insertion point to right after the closing brace to avoid nesting.
    const beforeIndex = Math.max(0, insertionIndex - 1);
    const startBrace = content.lastIndexOf('{', beforeIndex);
    if (startBrace !== -1) {
      const endBrace = content.indexOf('}', startBrace);
      if (
        endBrace !== -1 &&
        insertionIndex > startBrace &&
        insertionIndex <= endBrace
      ) {
        insertionIndex = endBrace + 1;
      }
    }

    textarea.focus();
    textarea.selectionStart = insertionIndex;
    textarea.selectionEnd = insertionIndex;

    if (!document.execCommand('insertText', false, newText)) {
      const updated =
        content.substring(0, insertionIndex) +
        newText +
        content.substring(insertionIndex);
      textarea.value = updated;
      // Ensure React onChange fires
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const newCaret = insertionIndex + newText.length;
    textarea.selectionStart = newCaret;
    textarea.selectionEnd = newCaret;
    textarea.focus();
  };

  const handleVariableSelect = (variable: string) => {
    insertAtCursor(variable);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block">
      <Button
        ref={buttonRef}
        variant={buttonVariant}
        size={buttonSize}
        className={cn(
          className ||
            'text-neutral-500 hover:text-neutral-700 flex items-center gap-2',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <BracketsCurly size={20} /> Add variable
      </Button>

      <div className="absolute" ref={menuContainerRef}>
        <VariablesDropdownMenu
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          promptVariables={variables}
          handleVariableSelect={handleVariableSelect}
          position={position}
        />
      </div>
    </div>
  );
};

export default AddVariableButton;
